// ConsentBanner — TCPA stale-consent warning + re-consent action.
//
// Renders below the lead header when:
//   - Consent record is missing entirely
//   - Consent capturedAt is older than 365 days (industry guidance)
//   - Consent.given is false (e.g. CSV imports, manual leads with no opt-in)
//
// One-click "Re-confirm consent" pushes to tcpaConsent.reConsent[] and resets
// the captured-at timestamp so the banner clears.

import React, { useState } from 'react';
import { AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../lib/api.js';

function consentStatus(lead) {
  const c = lead?.tcpaConsent;
  if (!c || !c.given) {
    return { level: 'critical', text: 'No TCPA consent on file. Do not auto-dial. Confirm consent before contact.' };
  }
  const captured = c.capturedAt || lead.createdAt;
  if (!captured) {
    return { level: 'warn', text: 'Consent timestamp missing. Verify before auto-dialing.' };
  }
  const ageDays = Math.floor((Date.now() - new Date(captured).getTime()) / 86400000);
  if (ageDays > 365) {
    return {
      level: 'warn',
      text: `Consent is ${ageDays} days old (>12 months). Recommend re-confirming before automated contact.`,
      ageDays,
    };
  }
  if (!c.authorizedAutoDial) {
    return { level: 'warn', text: 'Lead has SMS consent only — auto-dial NOT authorized at intake.' };
  }
  return null;
}

export default function ConsentBanner({ lead, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const status = consentStatus(lead);
  if (!status) return null;

  const palette = status.level === 'critical'
    ? 'bg-rose-500/10 border-rose-500/40 text-rose-200'
    : 'bg-amber-500/10 border-amber-500/40 text-amber-200';

  async function reConfirm() {
    setBusy(true);
    try {
      await apiPost(`/compliance/lead/${lead._id}/reconsent`, {
        method: 'call',
        notes: 'Agent verbally re-confirmed consent on lead detail screen',
      });
      toast.success('Consent re-confirmed');
      onUpdated?.();
    } catch (e) {
      toast.error(e.message || 'Failed to record re-consent');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${palette}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold mb-0.5">TCPA consent check</div>
          <div className="opacity-90">{status.text}</div>
          <button
            onClick={reConfirm}
            disabled={busy}
            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 disabled:opacity-50 transition text-[11px] font-medium"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
            Re-confirm consent
          </button>
        </div>
      </div>
    </div>
  );
}
